import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useBook } from '../hooks/useBooks';
import { Navbar } from '../components/layout/Navbar';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useUserProfile, isActiveSubscriber } from '../hooks/useUserProfile';
import { Lock } from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  setup: 'Setting up…',
  outline: 'Creating outline…',
  characters: 'Building characters…',
  writing: 'Writing chapters…',
  complete: 'Complete',
  error: 'Error',
};

const IN_PROGRESS_STATUSES = new Set(['setup', 'outline', 'characters', 'writing']);

interface Cover {
  id: string;
  publicUrl: string;
  variationIndex: number;
}

export function BookDetail() {
  const { bookId } = useParams<{ bookId: string }>();
  const queryClient = useQueryClient();

  const { data: book, isLoading, error } = useBook(bookId);
  const [retrying, setRetrying] = useState(false);
  const [premiseOpen, setPremiseOpen] = useState(false);

  const { data: profile } = useUserProfile();
  const isPaidUser = isActiveSubscriber(profile);

  const { data: coversData } = useQuery({
    queryKey: ['covers', bookId],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ covers: Cover[] }>>(`/books/${bookId}/covers`);
      return res.data.data?.covers ?? [];
    },
    enabled: !!bookId && !!book,
    staleTime: 60_000,
  });

  const selectedCover = coversData?.find((c) => c.id === book?.coverSelectedId) ?? null;

  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (book?.status === 'complete' && prevStatusRef.current !== 'complete') {
      void queryClient.invalidateQueries({ queryKey: ['books'] });
    }
    prevStatusRef.current = book?.status;
  }, [book?.status, queryClient]);

  const handleRetry = async () => {
    if (!bookId || retrying) return;
    setRetrying(true);
    try {
      await api.post(`/books/${bookId}/retry`);
      await queryClient.invalidateQueries({ queryKey: ['books', bookId] });
    } catch { /* next poll reflects state */ }
    finally { setRetrying(false); }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Book not found</p>
          <Link to="/dashboard" className="text-primary hover:underline">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const visibleComplete = isPaidUser ? book.chaptersComplete : Math.min(book.chaptersComplete, 1);
  const visibleTotal   = isPaidUser ? book.chaptersTotal    : Math.min(book.chaptersTotal, 1);
  const isGenerating   = IN_PROGRESS_STATUSES.has(book.status);
  // Free-tier books are marked 'complete' after ch1 with chaptersComplete < chaptersTotal.
  // Any completed book with at least 1 chapter written is readable in the editor.
  const canOpenEditor = (book.status === 'complete' || book.status === 'writing') && book.chaptersComplete >= 1;
  const isFullyComplete = book.status === 'complete' && book.chaptersComplete >= 1;
  const progress       = visibleTotal > 0 ? Math.round((visibleComplete / visibleTotal) * 100) : 0;

  const statusBadge = (
    <span className={`shrink-0 text-sm px-3 py-1 rounded-full ${
      book.status === 'complete' ? 'bg-green-900/30 text-green-400' :
      book.status === 'writing'  ? 'bg-blue-900/30 text-blue-400' :
      book.status === 'error'    ? 'bg-red-900/30 text-red-400' :
      'bg-yellow-900/30 text-yellow-400'
    }`}>
      {STATUS_LABELS[book.status] ?? book.status}
    </span>
  );

  const coverEl = selectedCover ? (
    <div className="relative group shrink-0 w-48">
      <img
        src={selectedCover.publicUrl}
        alt="Book cover"
        className="w-full rounded-lg shadow-xl object-cover"
        style={{ aspectRatio: '2/3' }}
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
        <Link
          to={`/books/${bookId}/covers`}
          className="px-3 py-1.5 bg-background/90 text-foreground rounded text-xs font-medium hover:bg-background transition-colors"
        >
          Change Cover
        </Link>
      </div>
    </div>
  ) : (
    <Link
      to={`/books/${bookId}/covers`}
      className="flex flex-col items-center justify-center w-48 border-2 border-dashed border-border rounded-lg bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors group shrink-0"
      style={{ aspectRatio: '2/3' }}
    >
      <span className="text-3xl mb-2">🎨</span>
      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">Add Cover</span>
    </Link>
  );

  const progressBar = visibleTotal > 0 ? (
    <div>
      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
        <span>{isGenerating ? (STATUS_LABELS[book.status] ?? 'Generating…') : 'Writing progress'}</span>
        <span>{visibleComplete} / {visibleTotal} chapters</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
    </div>
  ) : isGenerating ? (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
      {STATUS_LABELS[book.status] ?? 'Generating…'}
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-8 items-start">

          {/* ── Left column: cover + actions ── */}
          <div className="shrink-0 w-48 space-y-4">
            {coverEl}

            {book.status !== 'error' && (
              <div className="space-y-2">
                {canOpenEditor ? (
                  <Link
                    to={`/books/${bookId}/editor`}
                    className="block w-full text-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    {book.status === 'writing' ? 'Read as it writes' : 'Open Editor'}
                  </Link>
                ) : (
                  <div
                    title="Available once the first chapter is ready"
                    className="flex items-center justify-center gap-1.5 w-full px-4 py-2 bg-muted text-muted-foreground rounded-md text-sm font-medium cursor-not-allowed select-none"
                  >
                    <Lock className="h-3.5 w-3.5" />
                    Open Editor
                  </div>
                )}
                {isFullyComplete && (
                  <Link
                    to={`/kdp-walkthrough?bookId=${bookId}`}
                    className="block w-full text-center px-4 py-2 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm"
                  >
                    📖 KDP Walkthrough
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* ── Right column: info ── */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h1 className="text-3xl font-bold text-foreground leading-tight">{book.title}</h1>
              {statusBadge}
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {[book.genre, book.toneStyle, book.targetAudience, book.contentRating].map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded capitalize">{tag}</span>
              ))}
              <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                {book.wordCount.toLocaleString()} words
              </span>
              <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                {visibleComplete}/{visibleTotal} chapters
              </span>
            </div>

            {(isGenerating || visibleTotal > 0) && (
              <div className="mb-6">{progressBar}</div>
            )}

            {/* Collapsible premise */}
            <div className="border border-border rounded-lg bg-card overflow-hidden mb-6">
              <button
                onClick={() => setPremiseOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-accent/30 transition-colors"
              >
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Premise</span>
                <span className="text-muted-foreground text-xs">{premiseOpen ? '▲' : '▼'}</span>
              </button>
              {!premiseOpen && (
                <p className="px-4 pb-3 text-sm text-muted-foreground line-clamp-2">{book.premise}</p>
              )}
              {premiseOpen && (
                <div className="px-4 pb-4">
                  <p className="text-foreground leading-relaxed">{book.premise}</p>
                </div>
              )}
            </div>

            {/* Error state */}
            {book.status === 'error' && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive font-medium">Generation failed</p>
                {(book as unknown as { errorMessage?: string }).errorMessage && (
                  <p className="text-xs text-destructive/80 mt-1">
                    {(book as unknown as { errorMessage?: string }).errorMessage}
                  </p>
                )}
                <button
                  onClick={() => { void handleRetry(); }}
                  disabled={retrying}
                  className="mt-3 px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {retrying ? 'Retrying…' : '↺ Try Again'}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
